/**
 * Emptiness checks, so a guard can say what it means without leaning on truthiness.
 *
 * The house rule (STYLEGUIDE.md "Comparisons and emptiness") is that `if (!value)` is banned
 * for anything that is not already a boolean: it conflates null, undefined, "", 0, NaN and
 * false into one silent test, and the bug it hides is always the same shape - a legitimate
 * `0` or `""` taking the "missing" branch. `value == null` is the one sanctioned loose
 * comparison (it catches null AND undefined and nothing else); everything else is `===`.
 *
 * Spelling that out inline gets verbose fast - `x == null || x.length === 0` is three
 * concepts to read where "is it empty" is one - so these helpers carry it. Each returns a
 * type predicate, which means the FALSE branch narrows away null and undefined: after
 * `if (isEmptyArray(rows)) return;` the compiler knows `rows` is an array.
 *
 * The list grows on demand. Don't add a helper here until something needs it.
 */

/** null, undefined, or "" - narrows to `string` when false */
export function isEmptyString(value: string | null | undefined): value is null | undefined | "" {
	return value == null || value.length === 0;
}

/** null, undefined, "", or nothing but whitespace - for text a human typed */
export function isBlankString(value: string | null | undefined): value is null | undefined | "" {
	return value == null || value.trim().length === 0;
}

/** null, undefined, or [] - narrows to the array type when false */
export function isEmptyArray<T>(value: readonly T[] | null | undefined): value is null | undefined | readonly [] {
	return value == null || value.length === 0;
}

/** null, undefined, or an object with no own enumerable keys */
export function isEmptyRecord<T>(value: Record<string, T> | null | undefined): value is null | undefined | Record<string, never> {
	return value == null || Object.keys(value).length === 0;
}
